import { Box, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import CopyField from '~/components/Fields/CopyField';
import { DicomTag, hasLiteralValue } from '~/utils/dicomTags';
import { monospaceSx } from './dicomTagLayout';

/**
 * Fixed rather than fitted, so selecting a row never resizes the table above;
 * a longer value scrolls inside it.
 */
const VALUE_HEIGHT_PX = 64;

interface DicomTagValueDetailProps {
    tag: DicomTag | null;
    /** The value is masked: it is neither shown nor offered for copy. */
    isRedacted: boolean;
}

/**
 * The full value of one tag, wrapped over as many lines as it needs, with a
 * copy button when the value is the attribute's own text.
 */
export default function DicomTagValueDetail({
    tag,
    isRedacted,
}: DicomTagValueDetailProps) {
    const intl = useIntl();

    const value = (
        <Box
            sx={{
                ...monospaceSx,
                height: VALUE_HEIGHT_PX,
                overflowY: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                ...(isRedacted && { color: 'text.secondary' }),
            }}
        >
            {isRedacted ? (
                <FormattedMessage id="dicom.tagViewer.hiddenValue" />
            ) : (
                tag?.value
            )}
        </Box>
    );

    return (
        <Box
            role="region"
            aria-label={intl.formatMessage({ id: 'dicom.tagViewer.value' })}
            data-testid="dicom-tag-value-detail"
            sx={{ flexShrink: 0, p: 1, borderTop: 1, borderColor: 'divider' }}
        >
            <Typography fontSize={12} color="text.secondary" noWrap mb={0.5}>
                {tag ? (
                    `${tag.tag} ${tag.name} ${tag.vr}`
                ) : (
                    <FormattedMessage id="dicom.tagViewer.selectPrompt" />
                )}
            </Typography>
            {tag && !isRedacted && hasLiteralValue(tag) ? (
                <CopyField copyText={tag.value} fullWidth iconAlwaysVisible>
                    {value}
                </CopyField>
            ) : (
                value
            )}
        </Box>
    );
}
